// @ts-nocheck
/**
 * Native Mapbox GL track layer for the explore (single-source) map — the GL analogue of TrackLayer.
 * Flag-gated by config.MAP_GL_NATIVE; mounted by MapContainer instead of <TrackLayer> when native mode is on.
 *
 * Renders the track as two native `line` layers fed by GeoJSON (built by renderers/buildTrackFeatures):
 *   - base:      one LineString per merged (color,width) run; data-driven `line-color`/`line-width`.
 *   - selection: colored event-selection overlays on top.
 * Mapbox owns pan/zoom on the GPU, so there is NO per-frame map.project reprojection. Feature rebuild
 * runs only when data / maptype / time-window / selection / LOD-zoom-bucket changes.
 *
 * Interactivity is preserved natively: layer-scoped click/mousemove → nearest data point (by lngLat, no
 * reprojection) → onPointClick (seek) + setTooltip. Coloring reuses useTrackRendering unchanged.
 */

import { createEffect, onCleanup, untrack } from 'solid-js';
import { useTrackRendering } from '../../hooks/useTrackRendering';
import { buildTrackFeatures } from '../../renderers/buildTrackFeatures';
import { allSelectedEventIds, selectedEvents, selectedRanges, selectedRange, cutEvents, isCut, triggerSelection } from '../../../../../store/selectionStore';
import { selectedTime, timeWindow, isUnsetPlaybackTime } from '../../../../../store/playbackStore';
import { setTooltip } from '../../../../../store/globalStore';
import { persistantStore } from '../../../../../store/persistantStore';
import { defaultChannelsStore } from '../../../../../store/defaultChannelsStore';
import { getCurrentDatasetTimezone } from '../../../../../store/datasetTimezoneStore';
import { resolveSelectionRangeStrokeColor } from '../../../../../utils/colorScale';
import { selectionRangesSignature } from '../../../../../utils/selectionRangeHelpers';
import { formatTime } from '../../../../../utils/global';
import {
  getExpandedViewportBounds,
  getCombinedSampleRate,
  sampleTrack,
  mergeSelectionRangePoints,
} from '../../utils/lodUtils';
import { createTrackImportanceCache, trackSimplifyTolerance } from '../../utils/trackGeometry';

export interface GlMapTrackLayerProps {
  data: any[];
  map: any;
  maptype: string;
  samplingFrequency: number;
  onPointClick?: (point: any) => void;
  showGaps?: boolean;
  gapThreshold?: number;
  maneuversEnabled?: boolean;
  zoomLevel?: number;
  effectivePlaybackTime?: Date | null;
  sourceColorHex?: string;
  // Fired once, after Mapbox has actually painted the first non-empty track (see paintPending below).
  onTrackPainted?: () => void;
}

let instanceCounter = 0;

export default function GlMapTrackLayer(props: GlMapTrackLayerProps) {
  const uid = `explore-gl-track-${instanceCounter++}`;
  const baseSrc = `${uid}-base-src`;
  const baseLyr = `${uid}-base-lyr`;
  const selSrc = `${uid}-sel-src`;
  const selLyr = `${uid}-sel-lyr`;

  const { colorType: maptype } = persistantStore;
  const config: any = {
    maptype: maptype(),
    samplingFrequency: props.samplingFrequency,
    showGaps: props.showGaps ?? true,
    gapThreshold: props.gapThreshold,
  };

  const { initScales, getColor, getThickness, applyFilters, getTimestamp, sourceColor, sourceColorLoaded } =
    useTrackRendering(config);

  const { latName, lngName } = defaultChannelsStore;
  const getLat = (d: any): number | undefined => {
    if (!d) return undefined;
    const f = latName();
    const val = d[f] ?? d[f.toLowerCase()] ?? d[f.toUpperCase()] ?? d.Lat ?? d.lat;
    if (val === undefined || val === null) return undefined;
    const n = Number(val);
    return isNaN(n) ? undefined : n;
  };
  const getLng = (d: any): number | undefined => {
    if (!d) return undefined;
    const f = lngName();
    const val = d[f] ?? d[f.toLowerCase()] ?? d[f.toUpperCase()] ?? d.Lng ?? d.lng;
    if (val === undefined || val === null) return undefined;
    const n = Number(val);
    return isNaN(n) ? undefined : n;
  };

  const getColorWrapper = (d: any, prev: any, cfg: any) =>
    props.sourceColorHex ? props.sourceColorHex : getColor(d, prev, cfg);

  const tsAt = (d: any): number => getTimestamp(d).getTime();

  // ── Base cache: the full-day work that does NOT depend on the playhead (scale init, brush/cut filter,
  //    and a one-time time-sort) is recomputed only when data / mode / selection changes — NOT per scrub.
  //    Per-scrub work then binary-searches this sorted array, so a click/drag is O(window + log n). ──
  // Base = the FULL data, time-sorted, with scales initialized — cached across scrubs. Brush/cut/window
  // filtering happens per-rebuild in prepareData (below), NOT here, so it's robust + Datetime-consistent.
  let baseCache: { dataSig: string; sig: string; sorted: any[] } | null = null;
  // Content signature — trackData() hands us a fresh array each call, so key on data, not reference.
  const dataSig = (raw: any[]): string =>
    raw.length ? `${raw.length}_${tsAt(raw[0])}_${tsAt(raw[raw.length - 1])}` : 'empty';
  const baseSig = () => `${maptype()}|ev:${(allSelectedEventIds() || []).join('.')}`;
  const getBase = (raw: any[]): any[] => {
    const sig = baseSig();
    const ds = dataSig(raw);
    if (!baseCache || baseCache.dataSig !== ds || baseCache.sig !== sig) {
      initScales(raw, config);
      const sorted = [...raw].sort((a, b) => tsAt(a) - tsAt(b));
      baseCache = { dataSig: ds, sig, sorted };
    }
    return baseCache.sorted;
  };

  // first index with tsAt >= ms
  const lowerBound = (arr: any[], ms: number): number => {
    let lo = 0;
    let hi = arr.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (tsAt(arr[mid]) < ms) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };
  // first index with tsAt > ms
  const upperBound = (arr: any[], ms: number): number => {
    let lo = 0;
    let hi = arr.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (tsAt(arr[mid]) <= ms) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };

  // Interpolated track endpoint at the playhead over the (sorted) base — O(log n) bracket via binary search.
  const interpEndpointSorted = (sorted: any[], targetDate: Date): any | null => {
    const tMs = targetDate.getTime();
    const ub = upperBound(sorted, tMs);
    const prev = ub - 1 >= 0 ? sorted[ub - 1] : null;
    const next = ub < sorted.length ? sorted[ub] : null;
    if (prev && next) {
      const prevTs = tsAt(prev);
      const nextTs = tsAt(next);
      const la1 = getLat(prev);
      const ln1 = getLng(prev);
      const la2 = getLat(next);
      const ln2 = getLng(next);
      if (prevTs < nextTs && la1 !== undefined && ln1 !== undefined && la2 !== undefined && ln2 !== undefined) {
        const f = (tMs - prevTs) / (nextTs - prevTs);
        const lat = la1 + (la2 - la1) * f;
        const lng = ln1 + (ln2 - ln1) * f;
        const p: any = { ...prev, Datetime: targetDate, Lat: lat, Lng: lng, Lat_dd: lat, Lng_dd: lng };
        p[latName()] = lat;
        p[lngName()] = lng;
        return p;
      }
    }
    return null;
  };

  // Points currently rendered — used for nearest-point picking without reprojection.
  let preparedPoints: any[] = [];
  // LOD sample rate of the last rebuild (so gap sizing uses the effective, post-sampling interval).
  let lastSampleRate = 1;
  // Signature of the last rendered state — dedups rebuilds (incl. the 'idle' safety net) to avoid loops.
  let lastRenderSig = '';
  // RDP importance of the sorted track. Cached across scrubs; only the zoom tolerance changes.
  const trackImportance = createTrackImportanceCache();

  const styleReady = () => {
    const map = props.map;
    return !!map && typeof map.isStyleLoaded === 'function' && map.isStyleLoaded();
  };

  const emptyFc = () => ({ type: 'FeatureCollection', features: [] });

  const ensureLayers = (map: any) => {
    if (!map.getSource(baseSrc)) {
      map.addSource(baseSrc, { type: 'geojson', data: emptyFc() });
      map.addLayer({
        id: baseLyr,
        type: 'line',
        source: baseSrc,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': ['get', 'color'], 'line-width': ['get', 'width'] },
      });
    }
    if (!map.getSource(selSrc)) {
      map.addSource(selSrc, { type: 'geojson', data: emptyFc() });
      map.addLayer({
        id: selLyr,
        type: 'line',
        source: selSrc,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': ['get', 'color'], 'line-width': ['get', 'width'] },
      });
    }
  };

  const removeLayers = (map: any) => {
    try {
      for (const id of [baseLyr, selLyr]) if (map.getLayer(id)) map.removeLayer(id);
      for (const id of [baseSrc, selSrc]) if (map.getSource(id)) map.removeSource(id);
    } catch (_) {
      /* map torn down */
    }
  };

  const toMs = (x: any): number => (x instanceof Date ? x.getTime() : new Date(x).getTime());
  const rangesOf = (arr: any): Array<[number, number]> =>
    (Array.isArray(arr) ? arr : [])
      .filter((r) => r && typeof r !== 'number' && r.start_time != null && r.end_time != null)
      .map((r) => [toMs(r.start_time), toMs(r.end_time)] as [number, number]);
  const inRanges = (tMs: number, rs: Array<[number, number]>) => rs.some(([s, e]) => tMs >= s && tMs <= e);

  // ── Data prep: brush-first (brush overrides the time window and shows the full brushed range), then cut,
  //    then the trailing time window (or full at window=0). LOD → validate. Datetime-consistent via tsAt. ──
  const prepareData = (raw: any[]): any[] => {
    const sorted = getBase(raw); // full data, sorted + scales, cached across scrubs
    let filtered = sorted;

    const brushRanges = rangesOf(selectedRange());
    const cutActive = isCut();
    const cutRanges = cutActive ? rangesOf(cutEvents()) : [];

    if (brushRanges.length > 0) {
      // Brush selection: show the whole brushed range, independent of the playback window.
      filtered = sorted.filter((d) => inRanges(tsAt(d), brushRanges));
    } else if (cutRanges.length > 0) {
      filtered = sorted.filter((d) => inRanges(tsAt(d), cutRanges));
    } else {
      const timeForFilter = props.effectivePlaybackTime ?? selectedTime();
      const currentTimeWindow = Number(timeWindow()); // sync may hand us a string
      if (currentTimeWindow > 0 && timeForFilter && !isUnsetPlaybackTime(timeForFilter)) {
        const endMs = timeForFilter.getTime();
        const startMs = endMs - currentTimeWindow * 60 * 1000;
        // Binary-search the window bounds instead of scanning the whole day each scrub.
        const lo = lowerBound(sorted, startMs);
        const hi = upperBound(sorted, endMs);
        filtered = sorted.slice(lo, hi);
        const interp = interpEndpointSorted(sorted, timeForFilter);
        if (interp) {
          interp.__glEndpoint = true; // never gap-skip the edge into the playhead endpoint (boat head)
          filtered = [...filtered, interp];
        }
      }
      // currentTimeWindow === 0 → filtered stays the full sorted track.
    }

    const currentZoom = props.zoomLevel ?? props.map.getZoom();
    const viewportBounds = getExpandedViewportBounds(props.map);
    const lodInfo = getCombinedSampleRate(currentZoom, filtered, viewportBounds, getLat, getLng, 5000);
    // NOTE: unlike the SVG SegmentedTrackRenderer we do NOT double the sample rate for color modes — that
    // was a DOM-count optimization; GL has no per-point DOM. Doubling spread points ~2s apart which, with
    // gap detection at high zoom, fragmented the colored track before the boat.
    const sampleRate = lodInfo.sampleRate;
    lastSampleRate = sampleRate;
    if (sampleRate > 1 && filtered.length > 0) {
      const beforeSampling = filtered;
      filtered = sampleTrack(filtered, sampleRate, currentZoom);
      // buildTrackFeatures cuts selection overlays from this sampled array and drops any range left with
      // fewer than 2 coords, so re-add the points sampling removed from inside short selection windows.
      filtered = mergeSelectionRangePoints(beforeSampling, filtered, selectedRanges(), tsAt);
    }

    // Validate coordinates
    const valid = filtered.filter((p) => {
      const lat = getLat(p);
      const lng = getLng(p);
      return lat !== undefined && lng !== undefined && !isNaN(lat) && !isNaN(lng);
    });
    config.zoomLevel = currentZoom;
    return valid;
  };

  // First-paint latch for the maneuver overlay. setData() only queues features — Mapbox parses and paints
  // them a frame or more later — so we arm paintPending here and fire onTrackPainted from the 'idle'
  // handler below, which is the first point at which the track is genuinely on screen.
  let trackPainted = false;
  let paintPending = false;

  const rebuild = () => {
    try {
      rebuildInner();
    } catch (err) {
      // Never let a transient error freeze the track (the SVG renderer is likewise wrapped in try/catch).
       
      console.warn('[GlMapTrackLayer] rebuild failed:', err);
    }
  };

  const rebuildInner = () => {
    const map = props.map;
    // NOTE: do NOT gate on sourceColorLoaded() — sourceColor() already has a safe default, and blocking the
    // whole track until the source palette loads left the track invisible on init (only maneuvers showed)
    // on views where sourcesStore never reports ready. Render now; the color updates reactively when loaded.
    if (!map || !styleReady()) return;
    ensureLayers(map);

    const raw = props.data || [];

    // Dedup: skip when nothing that affects the output changed. Essential because rebuild also runs on the
    // map 'idle' event — without this, setData→repaint→idle→rebuild→setData would loop forever. The signature
    // captures every input (data, mode, playhead, window, brush/cut/events, zoom bucket, source color).
    const tf = props.effectivePlaybackTime ?? selectedTime();
    const zoom = props.zoomLevel ?? map.getZoom();
    const sig = [
      dataSig(raw),
      maptype(),
      tf instanceof Date ? tf.getTime() : 0,
      Number(timeWindow()),
      JSON.stringify(rangesOf(selectedRange())),
      JSON.stringify(isCut() ? rangesOf(cutEvents()) : []),
      (allSelectedEventIds() || []).join('.'),
      // selectedRanges is written asynchronously by the report page AFTER selectedEvents changes, so it must
      // be part of the signature — otherwise that second write computes an identical sig, early-returns here,
      // and the overlay is stuck one selection behind (the 'idle' rebuild below is deduped too, so it never
      // recovers). Matches GlMapMultiTrackLayer. Also catches color-only changes (re-index after a deselect).
      selectionRangesSignature(selectedRanges()),
      Math.round(zoom * 4) / 4,
      sourceColor(),
      props.sourceColorHex || '',
    ].join('|');
    if (sig === lastRenderSig) return;
    lastRenderSig = sig;

    if (raw.length < 2) {
      map.getSource(baseSrc)?.setData(emptyFc());
      map.getSource(selSrc)?.setData(emptyFc());
      preparedPoints = [];
      return;
    }

    // Opt-in diagnostic: set window.__glTrackDebug = true in the console, then scrub, to see each rebuild.
    if (typeof window !== 'undefined' && (window as any).__glTrackDebug) {
      const tf = props.effectivePlaybackTime ?? selectedTime();
      // eslint-disable-next-line no-console
      console.log('[GlMapTrackLayer] rebuild', {
        timeWindow: Number(timeWindow()),
        timeForFilter: tf instanceof Date ? tf.toISOString() : tf,
        rawLen: raw.length,
      });
    }

    const currentZoom = props.zoomLevel ?? map.getZoom();
    const effectiveConfig = {
      ...config,
      sourceColor: sourceColor(),
      zoomLevel: currentZoom,
      showGaps: currentZoom >= 16 ? config.showGaps : false,
    };

    const validPoints = prepareData(raw);
    preparedPoints = validPoints;
    const sortedBase = getBase(raw);
    const simplify = {
      importance: trackImportance(sortedBase, getLat, getLng, `${latName()}|${lngName()}`),
      tolerance: trackSimplifyTolerance(currentZoom),
    };
    if (typeof window !== 'undefined' && (window as any).__glTrackDebug) {
      // eslint-disable-next-line no-console
      console.log('[GlMapTrackLayer] windowed points:', validPoints.length);
    }
    if (validPoints.length < 2) {
      map.getSource(baseSrc)?.setData(emptyFc());
      map.getSource(selSrc)?.setData(emptyFc());
      return;
    }

    // LOD-adjusted sampling frequency for gap sizing (matches TrackLayer effectiveSamplingFrequency intent).
    const { base, selection } = buildTrackFeatures({
      data: validPoints,
      getColor: getColorWrapper,
      getThickness,
      config: effectiveConfig,
      getLat,
      getLng,
      getTimestamp,
      // Effective (post-LOD) sampling frequency so gap sizing matches the actual point spacing.
      samplingFrequency: props.samplingFrequency / (lastSampleRate || 1),
      cut: { isCut: isCut(), cutEvents: cutEvents() },
      selectedRanges: selectedRanges(),
      // Bind the selection order so a range's color comes from its event's index in allSelectedEventIds(), not
      // from its position in selectedRanges() (see resolveSelectionRangeStrokeColor).
      resolveRangeColor: (range, index, total) =>
        resolveSelectionRangeStrokeColor(range, index, total, allSelectedEventIds() || []),
      simplify,
    });

    map.getSource(baseSrc)?.setData(base);
    map.getSource(selSrc)?.setData(selection);
    if (!trackPainted && base?.features?.length) paintPending = true;
  };

  // ── Picking / tooltip (no reprojection: nearest by lngLat over prepared points) ──
  const nearestPoint = (lngLat: { lng: number; lat: number }): any | null => {
    if (!preparedPoints.length) return null;
    const cosLat = Math.cos((lngLat.lat * Math.PI) / 180) || 1;
    let best: any = null;
    let bestD = Infinity;
    for (const p of preparedPoints) {
      const lng = getLng(p);
      const lat = getLat(p);
      if (lng === undefined || lat === undefined) continue;
      const dx = (lng - lngLat.lng) * cosLat;
      const dy = lat - lngLat.lat;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    return best;
  };

  const timezone = () => getCurrentDatasetTimezone();
  const getTooltipContent = (point: any): string => {
    if (!point) return '';
    const { twsName, bspName, twdName, twaName } = defaultChannelsStore;
    const tws = point[twsName()] || '';
    const twd = point[twdName()] || '';
    const bsp = point[bspName()] || '';
    const twa = point[twaName()] || '';
    const gp50 = persistantStore.selectedClassName?.() === 'gp50';
    const configRow = gp50 ? `<tr><td>CONFIG</td><td>${point.Config || ''}</td></tr>` : '';
    return `<table class='table-striped'>
      <tr><td>TIME</td><td>${formatTime(point.Datetime, timezone())}</td></tr>
      <tr><td>TWS</td><td>${tws}</td></tr>
      <tr><td>TWD</td><td>${twd}</td></tr>
      <tr><td>BSP</td><td>${bsp}</td></tr>
      <tr><td>TWA</td><td>${twa}</td></tr>
      <tr><td>GRADE</td><td>${point.grade || point.Grade || ''}</td></tr>
      <tr><td>RACE</td><td>${point.race_number || point.Race_number || ''}</td></tr>
      <tr><td>LEG</td><td>${point.leg_number || point.Leg_number || ''}</td></tr>
      ${configRow}
      <tr><td>MANEUVER</td><td>${point.Maneuver_type || ''}</td></tr>
      </table>`;
  };

  const onClick = (e: any) => {
    const pt = nearestPoint(e.lngLat);
    if (pt && props.onPointClick) props.onPointClick(pt);
  };
  const onMove = (e: any) => {
    const map = props.map;
    if (map) map.getCanvas().style.cursor = 'pointer';
    const pt = nearestPoint(e.lngLat);
    if (!pt) return;
    setTooltip({
      visible: true,
      content: getTooltipContent(pt),
      x: e.originalEvent?.clientX ?? 0,
      y: e.originalEvent?.clientY ?? 0,
    });
  };
  const onLeave = () => {
    const map = props.map;
    if (map) map.getCanvas().style.cursor = '';
    setTooltip({ visible: false, content: '', x: 0, y: 0 });
  };

  const bindInteractions = (map: any) => {
    for (const lyr of [baseLyr, selLyr]) {
      map.on('click', lyr, onClick);
      map.on('mousemove', lyr, onMove);
      map.on('mouseleave', lyr, onLeave);
    }
  };
  const unbindInteractions = (map: any) => {
    if (!map) return;
    for (const lyr of [baseLyr, selLyr]) {
      map.off('click', lyr, onClick);
      map.off('mousemove', lyr, onMove);
      map.off('mouseleave', lyr, onLeave);
    }
  };

  // Rebuild whenever the map settles (load, pan/zoom end, and after any source setData repaints). This ties
  // the GL track to the map's render cycle the same way the Canvas boat/maneuver overlays are, so the track
  // is never left stale when the reactive effect timing lags (it always catches up to the CURRENT state).
  const onIdle = () => {
    rebuild();
    // 'idle' means the last repaint finished with nothing outstanding, so the features handed to setData in
    // a previous rebuild are now drawn. Release the maneuver overlay (one-shot).
    if (paintPending && !trackPainted) {
      trackPainted = true;
      paintPending = false;
      props.onTrackPainted?.();
    }
  };
  const bindRenders = (map: any) => {
    map.on('idle', onIdle);
  };

  // Setup once the style is ready, then bind interactions + render-cycle rebuilds.
  createEffect(() => {
    const map = props.map;
    if (!map) return;
    if (!styleReady()) {
      map.once('styledata', () => {
        ensureLayers(map);
        bindInteractions(map);
        bindRenders(map);
        rebuild();
      });
      return;
    }
    ensureLayers(map);
    bindInteractions(map);
    bindRenders(map);
  });

  // Rebuild features reactively — synchronously in the same reactive flush as the boat/maneuver effects
  // so a click/scrub updates them all together. Rebuild is cheap now (order-independent O(n) interp,
  // no per-frame sort) and guarded by try/catch. Reading these signals subscribes the effect.
  createEffect(() => {
    // track dependencies
    void props.data;
    void maptype();
    void selectedTime();
    void props.effectivePlaybackTime;
    void timeWindow();
    void allSelectedEventIds();
    void selectedRanges();
    void selectedRange();
    void cutEvents();
    void isCut();
    void triggerSelection();
    void sourceColorLoaded();
    void sourceColor();

    config.maptype = maptype();
    untrack(() => rebuild());
  });

  onCleanup(() => {
    const map = props.map;
    if (map) map.off('idle', onIdle);
    unbindInteractions(map);
    if (map) removeLayers(map);
  });

  return null;
}
